import { countConditions } from "@estelle/domain";

import { apiFetch } from "@/adapters/api/base";
import type {
  InventoryRepository,
  InventoryRow,
  ListQuery,
  LowStockPage,
  Page,
  StockAdjustment,
  StockByWarehousePage,
  StockLookupRow,
  StockUpdateInput,
  StockUpdateResult,
  WriteResult,
} from "@estelle/domain";

/**
 * Inventory reads and the one write, over HTTP (#171).
 *
 * Stock levels and low stock come from Postgres, filtered and sorted there —
 * `filters/sql.ts`'s `INVENTORY_FIELDS` is the SQL half of the same field
 * names `modules/inventory/fields.ts` declares. Adjustments reaches Shopify
 * with `inventoryAdjustQuantities` through write-through (X1);
 * `routes/inventory.ts`'s own field map is the SQL half of `fields.ts`'s
 * `ADJUSTMENT_FIELDS`. No fixture fallback: every method here is real, unlike
 * `createApiWarehouses`, which still leans on one for Transfers and In
 * Transit (#172).
 */

function listParams(query: ListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor !== undefined) params.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    params.set("search", query.search);
  }
  if (query.filter && countConditions(query.filter) > 0) {
    params.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    params.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return params;
}

async function get<T>(path: string, params: URLSearchParams): Promise<T> {
  const query = params.toString();
  const response = await apiFetch(
    `/api/inventory${path}${query ? `?${query}` : ""}`,
  );
  if (!response.ok) {
    throw new Error(
      `Inventory request failed: ${String(response.status)} on ${path || "/"}`,
    );
  }
  return (await response.json()) as T;
}

/**
 * `POST /api/inventory/adjustments` answers `{ ok, data | rejection }`
 * whether Shopify accepted the write or not (`routes/inventory.ts`'s
 * `HTTP_STATUS` map uses 4xx for a refusal, never a 2xx-with-error-body), so
 * this reads the body on every status rather than throwing on a non-2xx the
 * way `get()` above does — a 422 here is an answer, not a transport failure.
 */
async function createAdjustment(input: {
  variantId: string;
  warehouseId: string;
  delta: number;
  reason: string;
  note?: string;
}): Promise<WriteResult<StockAdjustment>> {
  const response = await apiFetch("/api/inventory/adjustments", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<StockAdjustment> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Stock adjustment failed: ${String(response.status)} on /adjustments`,
  );
}

/**
 * `PATCH /api/inventory/levels/:id/low-stock-threshold`, an ERP-only write
 * (#487): the threshold is ours, so there is no Shopify behind it. Like
 * `createAdjustment`, a refusal is a `{ ok: false, rejection }` body with a 4xx
 * status — an answer to show — and a 403 from `requirePermission`, which is
 * `{ error, permission }` and not a result, becomes a `forbidden` rejection
 * naming what was missing. Anything else is a transport failure and throws.
 */
async function setLowStockThreshold(input: {
  levelId: string;
  threshold: number | null;
}): Promise<WriteResult<InventoryRow>> {
  const path = `/levels/${encodeURIComponent(input.levelId)}/low-stock-threshold`;
  const response = await apiFetch(`/api/inventory${path}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ threshold: input.threshold }),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<InventoryRow> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  if (response.status === 403) {
    const refusal = result as { permission?: string } | null;
    return {
      ok: false,
      rejection: {
        kind: "forbidden",
        message: "You do not have permission to set thresholds.",
        permission: refusal?.permission ?? "inventory.lowstock.edit",
      },
    };
  }
  throw new Error(
    `Setting the threshold failed: ${String(response.status)} on ${path}`,
  );
}

/** Apply the Update stock lines (#602): a refusal is an answer, a 403 is a `forbidden` rejection. */
async function applyStockUpdate(
  input: StockUpdateInput,
): Promise<WriteResult<StockUpdateResult>> {
  const response = await apiFetch("/api/inventory/stock-update", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<StockUpdateResult> | null;
  if (result && typeof result === "object" && "ok" in result) return result;
  if (response.status === 403) {
    return {
      ok: false,
      rejection: {
        kind: "forbidden",
        message: "You do not have permission to change stock.",
        permission: "inventory.adjustment.create",
      },
    };
  }
  throw new Error(`Stock update failed: ${String(response.status)}`);
}

export function createApiInventory(): InventoryRepository {
  return {
    list: (query: ListQuery): Promise<Page<InventoryRow>> =>
      get<Page<InventoryRow>>("", listParams(query)),

    listByWarehouse: (query: ListQuery): Promise<StockByWarehousePage> =>
      get<StockByWarehousePage>("/by-warehouse", listParams(query)),

    listLowStock: (query: ListQuery): Promise<LowStockPage> =>
      get<LowStockPage>("/low-stock", listParams(query)),

    setLowStockThreshold,

    listAdjustments: (query: ListQuery): Promise<Page<StockAdjustment>> =>
      get<Page<StockAdjustment>>("/adjustments", listParams(query)),

    lookupStock: async (query) => {
      const params = new URLSearchParams({ warehouseId: query.warehouseId });
      if (query.search) params.set("search", query.search);
      if (query.skus) params.set("skus", query.skus.join(","));
      if (query.lowest !== undefined)
        params.set("lowest", String(query.lowest));
      return (
        await get<{ items: StockLookupRow[] }>("/stock-update/lookup", params)
      ).items;
    },

    applyStockUpdate,

    createAdjustment,
  };
}
